import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useLocations } from './useLocations';
import { useDeviceId } from './useDeviceId';

export interface Register {
  id: string;
  name: string;
  is_active: boolean;
  current_user_id?: string | null;
  current_user_name?: string | null;
}

export interface RegisterSession {
  id: string;
  register_id: string;
  register_name: string;
  location_id: string;
  user_id: string;
  opening_balance: number;
  cash_payments: number;
  cash_refunds: number;
  pay_ins: number;
  pay_outs: number;
  status: 'open' | 'paused' | 'closed';
  opened_at: string;
  paused_at?: string | null;
}

export function useRegisterSession() {
  const { user, profile } = useAuth();
  const { currentLocation } = useLocations();
  const { deviceId } = useDeviceId();
  const queryClient = useQueryClient();

  // Check if user is a cashier/employee (needs device validation)
  const isCashierRole = profile?.role === 'cashier' || profile?.role === 'employee';

  // Fetch user's active register session at current location
  const {
    data: activeSession,
    isLoading: sessionLoading,
    error: sessionError,
  } = useQuery({
    queryKey: ['register-session', user?.id, currentLocation?.id],
    queryFn: async () => {
      if (!user?.id || !currentLocation?.id) return null;

      const { data, error } = await supabase.rpc('get_active_register_session', {
        p_location_id: currentLocation.id,
      });

      if (error) {
        console.error('Error fetching active session:', error);
        return null;
      }

      // RPC returns an array, get the first (and only) row
      return data && data.length > 0 ? (data[0] as RegisterSession) : null;
    },
    enabled: !!user?.id && !!currentLocation?.id,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Fetch available registers at current location
  const {
    data: registers,
    isLoading: registersLoading,
    error: registersError,
  } = useQuery({
    queryKey: ['available-registers', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase.rpc('get_available_registers', {
        p_location_id: currentLocation.id,
      });

      if (error) {
        console.error('Error fetching registers:', error);
        return [];
      }

      return (data as Register[]) || [];
    },
    enabled: !!currentLocation?.id,
    staleTime: 1000 * 10, // 10 seconds - registers change more frequently
    refetchOnWindowFocus: true,
  });

  // Pause register session (for breaks)
  const pauseSession = useMutation({
    mutationFn: async () => {
      if (!activeSession?.id) {
        throw new Error('No active session to pause');
      }

      // Start break log
      const { error: breakError } = await supabase.rpc('start_break', {
        p_session_id: activeSession.id,
      });

      if (breakError) {
        console.error('Failed to log break start:', breakError);
        // Continue anyway - logging failure shouldn't prevent pause
      }

      const { error } = await supabase
        .from('cash_drawer_sessions')
        .update({
          status: 'paused',
          paused_at: new Date().toISOString(),
        })
        .eq('id', activeSession.id);

      if (error) throw new Error(error.message || 'Failed to pause session');

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['register-session', user?.id, currentLocation?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['available-registers', currentLocation?.id],
      });
    },
  });

  // Resume register session (after break) - requires PIN verification
  const resumeSession = useMutation({
    mutationFn: async ({ pin }: { pin: string }) => {
      if (!activeSession?.id) {
        throw new Error('No session to resume');
      }

      if (!user?.id) {
        throw new Error('Not authenticated');
      }

      // Verify the user's own PIN
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('pin_hash')
        .eq('id', user.id)
        .single();

      if (profileError) throw new Error('Could not verify PIN');

      if (!userProfile?.pin_hash) {
        throw new Error('No PIN set for your account');
      }

      // Use database function to verify PIN (bcrypt comparison)
      const { data: isValid } = await supabase.rpc('verify_pin', {
        p_pin: pin,
        p_hash: userProfile.pin_hash,
      });

      if (!isValid) {
        throw new Error('Invalid PIN');
      }

      // End break log (resumed by self, so no resumed_by)
      const { error: breakError } = await supabase.rpc('end_break', {
        p_session_id: activeSession.id,
        p_resumed_by: null,
      });

      if (breakError) {
        console.error('Failed to log break end:', breakError);
        // Continue anyway - logging failure shouldn't prevent resume
      }

      // PIN verified, resume the session
      const { error } = await supabase
        .from('cash_drawer_sessions')
        .update({
          status: 'open',
          paused_at: null,
        })
        .eq('id', activeSession.id);

      if (error) throw new Error(error.message || 'Failed to resume session');

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['register-session', user?.id, currentLocation?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['available-registers', currentLocation?.id],
      });
    },
  });

  // Open register mutation
  const openRegister = useMutation({
    mutationFn: async ({
      registerId,
      openingBalance,
    }: {
      registerId: string;
      openingBalance: number;
    }) => {
      if (!currentLocation?.id) {
        throw new Error('No location selected');
      }

      // For cashiers, device ID is required for validation
      if (isCashierRole && !deviceId) {
        throw new Error('Device identification required. Please reload the page.');
      }

      const { data, error } = await supabase.rpc('open_register_session', {
        p_register_id: registerId,
        p_location_id: currentLocation.id,
        p_opening_balance: openingBalance,
        p_device_id: isCashierRole ? deviceId : null,
      });

      if (error) {
        // Parse specific error messages
        if (error.message?.includes('already has an open register')) {
          throw new Error('You already have an open register session');
        }
        if (error.message?.includes('already in use')) {
          throw new Error('This register is already in use by another cashier');
        }
        if (error.message?.includes('Device not registered')) {
          throw new Error('This device is not registered. Please contact an administrator.');
        }
        if (error.message?.includes('different location')) {
          throw new Error('This device is registered to a different location.');
        }
        throw new Error(error.message || 'Failed to open register');
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate both queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['register-session', user?.id, currentLocation?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['available-registers', currentLocation?.id],
      });
    },
  });

  // Get available registers (not in use)
  const availableRegisters = registers?.filter((r) => !r.current_user_id) || [];

  // Get registers in use (with user info)
  const registersInUse =
    registers?.filter((r) => r.current_user_id && r.current_user_id !== user?.id) || [];

  return {
    // Session data
    activeSession,
    isRegisterOpen: !!activeSession && activeSession.status === 'open',
    isSessionPaused: activeSession?.status === 'paused',
    sessionLoading,
    sessionError,

    // Register data
    registers: registers || [],
    availableRegisters,
    registersInUse,
    registersLoading,
    registersError,

    // Mutations
    openRegister,
    pauseSession,
    resumeSession,

    // Helpers
    isLoading: sessionLoading || registersLoading,
  };
}
